import { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '@/components/admin/AdminLayout';
import { Tag, Save, ToggleLeft, ToggleRight, Percent, Type, Hash, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { getApiUrl } from '@/utils/api';

const API = getApiUrl();

const AdminUpsell = () => {
  const [config, setConfig] = useState({
    enabled: true,
    discount_percent: 15,
    title: "APROVEITE! {discount}% OFF NESSES PRODUTOS",
    subtitle: "Adicione mais produtos e economize no frete!",
    badge_text: "-{discount}%",
    max_products: 4
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/upsell-config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfig(response.data);
    } catch (error) {
      console.error('Error fetching config:', error);
      toast.error('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API}/admin/upsell-config`, config, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Configurações salvas com sucesso!');
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error('Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  // Preview with discount replaced
  const getPreviewText = (text) => {
    return text.replace('{discount}', config.discount_percent);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20">
          <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div data-testid="admin-upsell">
        <div className="flex items-center justify-between mb-6 lg:mb-8">
          <div className="flex items-center gap-3">
            <Tag className="h-8 w-8 text-primary" />
            <div>
              <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-white">
                Configurar Upsell
              </h1>
              <p className="text-zinc-400 text-sm mt-1">
                Configure os produtos com desconto no checkout
              </p>
            </div>
          </div>
          
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-primary hover:bg-primary/90 text-black font-bold px-6 py-3 flex items-center gap-2 disabled:opacity-50"
          >
            {saving ? (
              <div className="h-4 w-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            SALVAR
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Settings Panel */}
          <div className="space-y-6">
            {/* Enable/Disable Toggle */}
            <div className="bg-zinc-900/50 border border-white/10 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-heading text-lg font-bold text-white mb-1">
                    Ativar Upsell
                  </h3>
                  <p className="text-zinc-400 text-sm">
                    Mostrar produtos com desconto no checkout
                  </p>
                </div>
                <button
                  onClick={() => handleChange('enabled', !config.enabled)}
                  className={`p-2 rounded transition-colors ${
                    config.enabled ? 'bg-primary/20 text-primary' : 'bg-zinc-800 text-zinc-500'
                  }`}
                >
                  {config.enabled ? (
                    <ToggleRight className="h-8 w-8" />
                  ) : (
                    <ToggleLeft className="h-8 w-8" />
                  )}
                </button>
              </div>
            </div>

            {/* Discount Percent */}
            <div className="bg-zinc-900/50 border border-white/10 p-6">
              <label className="block mb-4">
                <span className="flex items-center gap-2 text-white font-bold mb-2">
                  <Percent className="h-4 w-4 text-primary" />
                  Desconto (%)
                </span>
                <input
                  type="number"
                  min="1"
                  max="90"
                  value={config.discount_percent}
                  onChange={(e) => handleChange('discount_percent', parseInt(e.target.value) || 0)}
                  className="w-full bg-zinc-800 border border-white/10 px-4 py-3 text-white text-2xl font-mono focus:border-primary focus:outline-none"
                />
              </label>
              <div className="flex gap-2 mt-4">
                {[5, 10, 15, 20, 25, 30].map((percent) => (
                  <button
                    key={percent}
                    onClick={() => handleChange('discount_percent', percent)}
                    className={`px-4 py-2 font-bold text-sm transition-colors ${
                      config.discount_percent === percent
                        ? 'bg-primary text-black'
                        : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                    }`}
                  >
                    {percent}%
                  </button>
                ))}
              </div>
            </div>

            {/* Title */}
            <div className="bg-zinc-900/50 border border-white/10 p-6">
              <label className="block">
                <span className="flex items-center gap-2 text-white font-bold mb-2">
                  <Type className="h-4 w-4 text-primary" />
                  Título do Anúncio
                </span>
                <input
                  type="text"
                  value={config.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  placeholder="APROVEITE! {discount}% OFF NESSES PRODUTOS"
                  className="w-full bg-zinc-800 border border-white/10 px-4 py-3 text-white focus:border-primary focus:outline-none"
                />
                <p className="text-zinc-500 text-xs mt-2">
                  Use {'{discount}'} para inserir o percentual automaticamente
                </p>
              </label>
            </div>

            {/* Subtitle */}
            <div className="bg-zinc-900/50 border border-white/10 p-6">
              <label className="block">
                <span className="flex items-center gap-2 text-white font-bold mb-2">
                  <Type className="h-4 w-4 text-zinc-400" />
                  Subtítulo
                </span>
                <input
                  type="text"
                  value={config.subtitle}
                  onChange={(e) => handleChange('subtitle', e.target.value)}
                  placeholder="Adicione mais produtos e economize no frete!"
                  className="w-full bg-zinc-800 border border-white/10 px-4 py-3 text-white focus:border-primary focus:outline-none"
                />
              </label>
            </div>

            {/* Badge Text */}
            <div className="bg-zinc-900/50 border border-white/10 p-6">
              <label className="block">
                <span className="flex items-center gap-2 text-white font-bold mb-2">
                  <Tag className="h-4 w-4 text-primary" />
                  Texto do Badge
                </span>
                <input
                  type="text"
                  value={config.badge_text}
                  onChange={(e) => handleChange('badge_text', e.target.value)}
                  placeholder="-{discount}%"
                  className="w-full bg-zinc-800 border border-white/10 px-4 py-3 text-white focus:border-primary focus:outline-none"
                />
              </label>
            </div>

            {/* Max Products */}
            <div className="bg-zinc-900/50 border border-white/10 p-6">
              <label className="block">
                <span className="flex items-center gap-2 text-white font-bold mb-2">
                  <Hash className="h-4 w-4 text-primary" />
                  Quantidade de Produtos
                </span>
                <select
                  value={config.max_products}
                  onChange={(e) => handleChange('max_products', parseInt(e.target.value))}
                  className="w-full bg-zinc-800 border border-white/10 px-4 py-3 text-white focus:border-primary focus:outline-none"
                >
                  {[2, 3, 4, 5, 6].map((num) => (
                    <option key={num} value={num}>
                      {num} produtos
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>

          {/* Preview Panel */}
          <div className="lg:sticky lg:top-24">
            <div className="bg-zinc-900/50 border border-white/10 p-6">
              <div className="flex items-center gap-2 mb-4">
                <Eye className="h-5 w-5 text-primary" />
                <h3 className="font-heading text-lg font-bold text-white">
                  Pré-visualização
                </h3>
              </div>

              {config.enabled ? (
                <div className="bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/30 p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Tag className="h-4 w-4 text-primary" />
                    <h4 className="font-heading text-sm font-bold text-white">
                      {getPreviewText(config.title)}
                    </h4>
                  </div>
                  
                  <p className="text-zinc-400 text-xs mb-4">
                    {config.subtitle}
                  </p>

                  <div className="grid grid-cols-2 gap-2">
                    {Array.from({ length: Math.min(config.max_products, 4) }).map((_, i) => (
                      <div key={i} className="bg-zinc-900/80 border border-white/10 p-2">
                        <div className="relative mb-2">
                          <div className="w-full aspect-square bg-zinc-800 flex items-center justify-center">
                            <span className="text-zinc-600 text-xs">Produto</span>
                          </div>
                          <div className="absolute top-1 right-1 bg-primary text-black text-[8px] font-bold px-1">
                            {getPreviewText(config.badge_text)}
                          </div>
                        </div>
                        <div className="text-[10px] font-bold text-white truncate">
                          Nome do Produto
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-zinc-500 text-[8px] line-through">
                            R$ 100,00
                          </span>
                          <span className="text-primary font-mono text-[10px] font-bold">
                            R$ {(100 * (1 - config.discount_percent / 100)).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="bg-zinc-800 border border-zinc-700 p-8 text-center">
                  <EyeOff className="h-12 w-12 text-zinc-600 mx-auto mb-3" />
                  <p className="text-zinc-500 text-sm">
                    Upsell desativado
                  </p>
                  <p className="text-zinc-600 text-xs mt-1">
                    Os produtos não serão mostrados no checkout
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminUpsell;
